import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'shared-ui';

import { QueryKeyEnum } from 'core/global.enum';
import { useDodSocket } from 'hooks/useDodSocket/useDodSocket';

import { Dod } from './Dod';
import { DodOutletContext, DodToolbarOptions } from './Dod.types';
import { useDodCapabilities } from './hooks/useDodCapabilities/useDodCapabilities';

/**
 * Layout route for the DOD monitor.
 *
 * v1 mounted this container once per tab, which tore down the socket subscription on every tab
 * switch. It is now the parent of all six tabs and renders them through <Outlet />, so the socket,
 * the capability probe and the toolbar survive navigation.
 */
export const DodContainer = (): JSX.Element => {
  const queryClient = useQueryClient();
  const { connectionStatus, historyEntries } = useDodSocket();
  const { capabilities } = useDodCapabilities();
  const [refreshToken, setRefreshToken] = useState(0);
  const [toolbarOptions, setToolbarOptions] = useState<DodToolbarOptions>({});
  const [details, setDetails] = useState<ReactNode | null>(null);

  const onRefresh = useCallback(() => {
    setRefreshToken((token) => token + 1);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_STATUS]);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_OVERVIEW]);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_TIMESERIES]);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_QUEUES]);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_DLQ]);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_RETRIES]);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_WORKER]);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_HISTORY]);
  }, [queryClient]);

  const outletContext = useMemo<DodOutletContext>(
    () => ({
      connectionStatus,
      isSocketConnected: connectionStatus === 'connected',
      historyEntries,
      refreshToken,
      onRefresh,
      capabilities,
      setToolbarOptions,
      setDetails,
    }),
    [connectionStatus, historyEntries, refreshToken, onRefresh, capabilities],
  );

  return (
    <Dod
      connectionStatus={connectionStatus}
      onRefresh={onRefresh}
      outletContext={outletContext}
      toolbarOptions={toolbarOptions}
      details={details}
    />
  );
};
